/* Items — the commercially meaningful unit.
 *
 * A job carries a component count; the item lines behind it are generated from
 * a typical kitchen's mix so every job on the board has a plausible cut list to
 * open. When the API lands, `itemsFor` is the only thing that changes: the rest
 * of this file is real domain arithmetic (m², dominant edge profile) that the
 * quoting projection in Phase 3 will read as-is. */
import type { EdgeProfile, Item, Job, Substrate } from '@/types';

import { SHEENS, coatsFor } from '@/data/reference';

/** As much of a job as the item arithmetic reads. Narrower than a `Job` on
 *  purpose: everything here has to keep working when the item lines become
 *  records of their own and stop being generated from a component count. */
type JobSpec = Pick<Job, 'code' | 'colour' | 'sheen' | 'items' | 'profile'>;

interface RecipeLine {
  type: Item['type'];
  substrate: Substrate;
  w: number;
  h: number;
  faces: 1 | 2;
  edge: EdgeProfile;
  /** Fraction of the job's component count this line accounts for. */
  share: number;
}

/** What a kitchen is actually made of, by component count. */
const RECIPE: RecipeLine[] = [
  { type: 'Door', substrate: 'MR MDF', w: 720, h: 397, faces: 2, edge: 'Square', share: 0.33 },
  { type: 'Door', substrate: 'MR MDF', w: 720, h: 297, faces: 2, edge: 'Square', share: 0.15 },
  { type: 'Door', substrate: 'MR MDF', w: 2100, h: 597, faces: 2, edge: 'Shaker', share: 0.06 },
  {
    type: 'Drawer front',
    substrate: 'MR MDF',
    w: 140,
    h: 897,
    faces: 1,
    edge: 'Square',
    share: 0.18,
  },
  {
    type: 'Drawer front',
    substrate: 'MR MDF',
    w: 280,
    h: 897,
    faces: 1,
    edge: 'Square',
    share: 0.09,
  },
  {
    type: 'End panel',
    substrate: 'MR MDF',
    w: 2400,
    h: 600,
    faces: 1,
    edge: 'Square',
    share: 0.07,
  },
  {
    type: 'Kick',
    substrate: 'Standard MDF',
    w: 150,
    h: 3600,
    faces: 1,
    edge: 'Square',
    share: 0.07,
  },
  {
    type: 'Shelf',
    substrate: 'MR MDF',
    w: 380,
    h: 880,
    faces: 2,
    edge: 'Pencil round',
    share: 0.05,
  },
];

/** The item lines behind a job's component count. */
export function itemsFor(job: JobSpec): Item[] {
  const finish = SHEENS[job.sheen];
  const coats = coatsFor(job.sheen);
  // Numbered at the end, once the recipe knows how many lines it made.
  const lines: Omit<Item, 'id' | 'no'>[] = [];
  let left = job.items;

  RECIPE.forEach((r, i) => {
    if (left <= 0) return;
    // The last line mops up the remainder so the counts always add up.
    const want =
      i === RECIPE.length - 1 ? left : Math.min(left, Math.max(1, Math.round(r.share * job.items)));
    if (want <= 0) return;
    // Kicks and shelves keep the recipe's own profile; everything else takes
    // the profile the job was ordered in.
    const edge =
      r.type === 'Kick' || r.type === 'Shelf' ? r.edge : (job.profile as EdgeProfile) || r.edge;
    lines.push({
      type: r.type,
      substrate: r.substrate,
      w: r.w,
      h: r.h,
      faces: r.faces,
      edge,
      colour: job.colour,
      finish,
      coats,
      qty: want,
    });
    left -= want;
  });

  if (left > 0 && lines.length) lines[0].qty += left;
  return renumber(lines.map((l, i) => ({ ...l, id: i, no: 0 })));
}

/** Item numbers run 1..n in list order, so a job sheet reads like a cut list
 *  and never has a hole in it. Call this after anything that adds, duplicates
 *  or deletes a line — the id is the identity, the number is what people say. */
export function renumber(rows: Item[]): Item[] {
  return rows.map((r, i) => (r.no === i + 1 ? r : { ...r, no: i + 1 }));
}

/** The next free row id. `Date.now()` collides when someone leans on Duplicate
 *  last row, and two lines sharing an id edit as one. */
export function nextRowId(rows: Item[]): number {
  return rows.reduce((a, r) => Math.max(a, r.id), 0) + 1;
}

/** Sprayed area of one item line, in m². Two faces doubles it. */
export function sqmOf(r: Pick<Item, 'w' | 'h' | 'qty' | 'faces'>): number {
  return ((r.w * r.h) / 1e6) * r.qty * (r.faces === 2 ? 2 : 1);
}

/** The edge profile a job is mostly made of, plus a count of any others. A card
 *  has room for one profile; this says when that is not the whole story. */
export function profileOf(job: JobSpec): {
  main: string;
  mixed: string;
} {
  const tally: Record<string, number> = {};
  itemsFor(job).forEach((r) => {
    tally[r.edge] = (tally[r.edge] ?? 0) + r.qty;
  });
  const kinds = Object.keys(tally).sort((a, b) => tally[b] - tally[a]);
  const n = kinds.length - 1;
  return { main: kinds[0] ?? '—', mixed: n > 0 ? `+${n} ${n === 1 ? 'other' : 'others'}` : '' };
}

/* Total sprayed area per job. Cached because the board asks for it once per
   card per render and it is derived, not stored. */
const AREA = new Map<string, number>();

export function areaFor(job: JobSpec): number {
  const hit = AREA.get(job.code);
  if (hit !== undefined) return hit;
  const total = itemsFor(job).reduce((a, r) => a + sqmOf(r), 0);
  AREA.set(job.code, total);
  return total;
}

/** Drop a job's cached area — call after its item lines or count change. */
export function invalidateArea(code: string): void {
  AREA.delete(code);
}
